// Single source of truth for USD display formatting across the dashboard.
// Different surfaces had drifted into different rounding conventions
// (KPI 2dp vs Usage 4dp vs Top agents 4dp), which made the same number
// look different across pages and prompted "the values don't match"
// reports. Centralising the formatter makes alignment automatic.
//
// Convention (SaaS standard):
//   • Aggregates / KPIs / per-item rows: 2 decimal places (`$9.56`).
//     Mirrors Stripe / Vercel / AWS dashboards where summary numbers are
//     scanned, not analysed.
//   • Tiny values (< 1¢): 4 decimal places (`$0.0042`). Without this,
//     a brand-new tenant whose cost is $0.0050 would display as $0.00,
//     hiding real activity.
//   • Per-call / trace rows: caller passes precise = true to get 4dp
//     unconditionally — needed in Conversations trace where each call
//     costs fractions of a cent and the operator wants exact figures.
//
// Locale is fixed to en-US so the decimal/thousands separators are stable
// (`$1,234.56`, not `$1.234,56`) regardless of the viewer's browser
// locale — the dashboard is currency-pinned to USD anyway, and varying
// the separator across logged-in users would itself be confusing.

const STANDARD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PRECISE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

export interface FormatUsdOptions {
  // Force 4-decimal precision regardless of magnitude. Used by per-call
  // surfaces (Conversations trace, Usage page agent breakdown) where the
  // operator is auditing exact spend, not scanning.
  precise?: boolean;
}

export function formatUsd(amount: number, opts: FormatUsdOptions = {}): string {
  if (opts.precise) return PRECISE.format(amount);
  if (amount === 0) return '$0.00';
  // Below 1¢: 2-decimal rounding would show "$0.00" and hide the value.
  // Promote to 4dp for visibility — same trick Stripe uses on micro-spend
  // line items.
  if (Math.abs(amount) < 0.01) return PRECISE.format(amount);
  return STANDARD.format(amount);
}

